<template>
  <div class="panel">
    <header class="page-header">
      <div>
        <h1>设计中心</h1>
        <p>{{ subtitle }}<template v-if="artApiStatus.drafts"></template><template v-else-if="artModuleLoading.drafts"> · 加载中…</template></p>
      </div>
      <button class="btn-primary" @click="openNewStandalone">+ 新建设计</button>
    </header>

    <div class="toolbar">
      <div class="design-tabs" role="tablist" aria-label="切换设计稿件类型">
        <button
          type="button"
          role="tab"
          :aria-selected="activeTab === 'saved'"
          :class="{ active: activeTab === 'saved' }"
          @click="selectTab('saved')"
        >已保存设计 <span class="tab-count">{{ savedCount }}</span></button>
        <button
          type="button"
          role="tab"
          :aria-selected="activeTab === 'drafts'"
          :class="{ active: activeTab === 'drafts' }"
          @click="selectTab('drafts')"
        >草稿稿件 <span class="tab-count">{{ draftCount }}</span></button>
      </div>

      <div class="search-bar">
        <input
          v-model="searchInput"
          type="search"
          class="search-input"
          :placeholder="searchPlaceholder"
          aria-label="搜索设计"
          @keydown.enter.prevent="doSearch"
        >
        <button
          v-if="searchInput.trim()"
          type="button"
          class="btn-clear"
          aria-label="清空搜索"
          @click="clearSearch"
        >清空</button>
        <button type="button" class="btn-search" aria-label="搜索" @click="doSearch">
          <Search :size="16" />
        </button>
      </div>
    </div>

    <table v-if="activeTab === 'saved'" class="data-table">
      <thead>
        <tr>
          <th>设计名称</th>
          <th v-if="isDesigner">订单号</th>
          <th v-if="isDesigner">曲名</th>
          <th>模版</th>
          <th>店铺</th>
          <th>更新时间</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="d in paginatedSavedDesigns" :key="d.id">
          <td><strong>{{ displayName(d) }}</strong></td>
          <td v-if="isDesigner">{{ d.orderId || '—' }}</td>
          <td v-if="isDesigner" class="song-cell" :title="d.songName">{{ d.songName || '—' }}</td>
          <td>{{ d.templateName || '—' }}</td>
          <td>{{ storeName(d.storeId) }}</td>
          <td>{{ d.updated || '—' }}</td>
          <td class="row-actions">
            <div class="row-actions-inner">
              <button
                class="btn-sm"
                :disabled="Boolean(openingDesignId)"
                @click="openDesign(d)"
              >{{ openingDesignId === String(d.id) ? '打开中…' : '打开' }}</button>
              <button class="btn-sm danger" @click="askDeleteSaved(d)">删除</button>
            </div>
          </td>
        </tr>
        <tr v-if="!filteredSavedDesigns.length">
          <td :colspan="isDesigner ? 7 : 5" class="empty">
            <WhaleEmpty
              compact
              size="small"
              :variant="activeQuery ? 'search' : 'default'"
              :title="activeQuery ? '未找到匹配的设计' : '暂无已保存设计'"
              :description="activeQuery ? '未找到匹配的设计，请尝试更换关键词搜索' : '暂无已保存设计，点击「新建设计」开始'"
              :action-text="activeQuery ? '重置搜索' : '新建设计'"
              @action="activeQuery ? clearSearch() : openNewStandalone()"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <table v-else class="data-table">
      <thead>
        <tr>
          <th>设计名称</th>
          <th v-if="isDesigner">订单号</th>
          <th v-if="isDesigner">曲名</th>
          <th>模版</th>
          <th>店铺</th>
          <th>更新时间</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="d in paginatedDrafts" :key="d.id">
          <td><strong>{{ displayName(d) }}</strong></td>
          <td v-if="isDesigner">{{ d.orderId || '—' }}</td>
          <td v-if="isDesigner" class="song-cell" :title="d.songName">{{ d.songName || '—' }}</td>
          <td>{{ d.templateName || '—' }}</td>
          <td>{{ storeName(d.storeId) }}</td>
          <td>{{ d.updated || d.savedAt || '—' }}</td>
          <td class="row-actions">
            <div class="row-actions-inner">
              <button
                class="btn-sm primary"
                :disabled="Boolean(openingDesignId)"
                @click="openDesign(d)"
              >{{ openingDesignId === String(d.id) ? '打开中…' : '打开' }}</button>
              <button class="btn-sm danger" @click="askDeleteDraft(d)">删除</button>
            </div>
          </td>
        </tr>
        <tr v-if="!filteredDrafts.length">
          <td :colspan="isDesigner ? 7 : 5" class="empty">
            <WhaleEmpty
              compact
              size="small"
              :variant="activeQuery ? 'search' : 'default'"
              :title="activeQuery ? '未找到匹配的草稿' : '暂无草稿'"
              :description="activeQuery ? '未找到匹配的草稿，请尝试更换关键词搜索' : '暂无草稿稿件'"
              :action-text="activeQuery ? '重置搜索' : ''"
              @action="onEmptyAction"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <!-- 现代化精致底部分页条 -->
    <PlatformPagination
      v-model:current-page="pageNum"
      v-model:page-size="pageSize"
      :total="total"
      item-name="条"
    />

    <SystemDialog
      :open="dialog.open"
      :title="dialog.title"
      :message="dialog.message"
      :confirm-text="dialog.confirmText"
      :danger="dialog.danger"
      @cancel="closeDialog"
      @confirm="confirmDialog"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { Search } from '@lucide/vue';
import { usePlatformStore } from '@/composables/usePlatformStore';
import SystemDialog from './SystemDialog.vue';
import { PlatformPagination, WhaleEmpty } from '@/components/common';

const {
  platform,
  currentStore,
  allSavedDesigns,
  allDrafts,
  openNewStandalone,
  openDesign,
  openingDesignId,
  deleteSavedDesign,
  deleteDraft,
  roleKey,
  artApiStatus,
  artModuleLoading,
  refreshDesignCenterTab,
  useRemoteDesignCenter,
} = usePlatformStore();

const activeTab = ref('saved');
const searchInput = ref('');
const pageNum = ref(1);
const pageSize = ref(10);

const dialog = ref({
  open: false,
  title: '',
  message: '',
  confirmText: '确认删除',
  danger: true,
  type: '',
  target: null,
});

const savedCount = computed(() => allSavedDesigns().length);
const draftCount = computed(() => allDrafts().length);

const isDesigner = computed(() => roleKey.value === 'designer');
const activeQuery = computed(() => searchInput.value.trim());

const subtitle = computed(() => {
  const store = currentStore.value?.name || '鲸夕总店';
  if (isDesigner.value) {
    return `${store} · 所有保存的设计和草稿`;
  }
  return `${store} · 个人设计工作台`;
});

const searchPlaceholder = computed(() => {
  if (activeTab.value === 'saved') {
    return isDesigner.value ? '搜索设计名称、订单号、曲名或模版' : '搜索设计名称或模版';
  }
  return isDesigner.value ? '搜索草稿名称、订单号、曲名或模版' : '搜索草稿名称或模版';
});

function displayName(d) {
  if (d.orderId && d.songName) {
    return d.designName || d.songName || d.templateName || '未命名设计';
  }
  return d.designName || d.templateName || '未命名设计';
}

function storeName(id) {
  if (!id || id === 'all') return '平台通用';
  const cleanId = String(id).replace(/^s/i, '');
  const match = platform.stores.find(s =>
    s.id === id
    || s.id === `s${cleanId}`
    || String(s.departmentId) === cleanId
    || String(s.id) === cleanId
  );
  if (match?.name || match?.departmentName) return match.name || match.departmentName;
  if (cleanId === '1') return '鲸夕总店';
  return `店铺${cleanId}`;
}

function matchesSearch(d, query) {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  const name = displayName(d).toLowerCase();
  const template = (d.templateName || '').toLowerCase();
  const orderId = (d.orderId || '').toLowerCase();
  const songName = (d.songName || '').toLowerCase();

  return name.includes(q) || template.includes(q) || orderId.includes(q) || songName.includes(q);
}

function buildDesignCenterListOptions() {
  const query = activeQuery.value;
  return {
    searchQuery: query || undefined,
    pageNum: pageNum.value,
    pageSize: query ? pageSize.value : 200,
  };
}

async function reloadDesignCenterList() {
  if (!useRemoteDesignCenter) return;
  await refreshDesignCenterTab(activeTab.value, buildDesignCenterListOptions());
}

async function doSearch() {
  searchInput.value = searchInput.value.trim();
  pageNum.value = 1;
  await reloadDesignCenterList();
}

async function clearSearch() {
  searchInput.value = '';
  pageNum.value = 1;
  await reloadDesignCenterList();
}

function onEmptyAction() {
  if (activeQuery.value) {
    clearSearch();
    return;
  }
  if (activeTab.value === 'saved') {
    openNewStandalone();
  }
}

watch(searchInput, () => {
  pageNum.value = 1;
});

watch([pageNum, pageSize], () => {
  if (activeQuery.value && useRemoteDesignCenter) {
    reloadDesignCenterList();
  }
});

async function selectTab(tab) {
  activeTab.value = tab;
  searchInput.value = '';
  pageNum.value = 1;
  await refreshDesignCenterTab(tab);
}

const filteredSavedDesigns = computed(() => {
  const list = allSavedDesigns();
  if (useRemoteDesignCenter && activeQuery.value) return list;
  if (!activeQuery.value) return list;
  return list.filter(d => matchesSearch(d, activeQuery.value));
});

const filteredDrafts = computed(() => {
  const list = allDrafts();
  if (useRemoteDesignCenter && activeQuery.value) return list;
  if (!activeQuery.value) return list;
  return list.filter(d => matchesSearch(d, activeQuery.value));
});

const currentList = computed(() => (activeTab.value === 'saved' ? filteredSavedDesigns.value : filteredDrafts.value));
const total = computed(() => {
  if (activeQuery.value && useRemoteDesignCenter) {
    const remoteTotal = activeTab.value === 'saved'
      ? platform.designCenterSearchTotals?.saved
      : platform.designCenterSearchTotals?.drafts;
    if (remoteTotal != null) return remoteTotal;
  }
  if (activeQuery.value) {
    return currentList.value.length;
  }
  if (activeTab.value === 'saved') {
    return allSavedDesigns().length;
  }
  return allDrafts().length;
});
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)));

const paginatedSavedDesigns = computed(() => {
  if (useRemoteDesignCenter && activeQuery.value) {
    return filteredSavedDesigns.value;
  }
  const start = (pageNum.value - 1) * pageSize.value;
  return filteredSavedDesigns.value.slice(start, start + pageSize.value);
});

const paginatedDrafts = computed(() => {
  if (useRemoteDesignCenter && activeQuery.value) {
    return filteredDrafts.value;
  }
  const start = (pageNum.value - 1) * pageSize.value;
  return filteredDrafts.value.slice(start, start + pageSize.value);
});

function askDeleteSaved(d) {
  dialog.value = {
    open: true,
    title: '删除设计',
    message: `确定删除「${displayName(d)}」吗？删除后无法恢复。`,
    confirmText: '确认删除',
    danger: true,
    type: 'saved',
    target: d,
  };
}

function askDeleteDraft(d) {
  dialog.value = {
    open: true,
    title: '删除草稿',
    message: `确定删除草稿「${displayName(d)}」吗？`,
    confirmText: '确认删除',
    danger: true,
    type: 'draft',
    target: d,
  };
}

function closeDialog() {
  dialog.value.open = false;
  dialog.value.target = null;
}

function confirmDialog() {
  const { type, target } = dialog.value;
  if (!target) {
    closeDialog();
    return;
  }
  if (type === 'saved') deleteSavedDesign(target);
  else if (type === 'draft') deleteDraft(target);
  closeDialog();
}
</script>

<style scoped>
.panel { padding: 24px; }
.page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
.page-header h1 { font-size: 22px; font-weight: 700; }
.page-header p { font-size: 13px; color: var(--text-muted); margin-top: 4px; }
.btn-primary { padding: 8px 18px; border-radius: var(--radius-round); background: #2c2c2c; color: #fff; font-size: 13px; }
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.design-tabs {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-round);
  background: #fff;
  flex-shrink: 0;
}
.design-tabs button {
  min-width: 112px;
  padding: 8px 16px;
  border-radius: var(--radius-round);
  color: var(--text-muted);
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}
.design-tabs button:hover { color: var(--text-dark); }
.design-tabs button.active { background: var(--bg-active-green); color: #6d28d9; }
.tab-count {
  font-size: 11px;
  background: var(--bg-grey-light);
  color: var(--text-muted);
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
}
.design-tabs button.active .tab-count {
  background: rgba(22, 128, 60, 0.15);
  color: #6d28d9;
}
.search-bar {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
}
.search-input {
  width: 240px;
  padding: 7px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-round);
  font-size: 13px;
  background: #fff;
}
.search-input:focus {
  outline: none;
  border-color: var(--accent-green);
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
}
.btn-search {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-round);
  background: #fff;
  color: var(--text-muted);
}
.btn-search:hover {
  border-color: var(--accent-green);
  color: var(--accent-green);
  background: #f6fffb;
}
.btn-clear {
  flex-shrink: 0;
  height: 34px;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-round);
  background: #fff;
  color: var(--text-muted);
  font-size: 12px;
}
.btn-clear:hover {
  border-color: #ffa39e;
  color: #cf1322;
  background: #fff1f0;
}
.data-table { width: 100%; border-collapse: collapse; background: #fff; border-radius: 12px; overflow: hidden; border: 1px solid var(--border-color, #e2e8f0); margin-bottom: 8px; table-layout: fixed; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04); }
.data-table th, .data-table td { padding: 12px 14px; text-align: left; font-size: 13px; border-bottom: 1px solid var(--border-color, #f1f5f9); vertical-align: middle; }
.data-table th { background: #f8fafc; font-weight: 600; color: var(--text-muted, #64748b); font-size: 12px; }
.data-table tbody tr { transition: background 0.15s ease; }
.data-table tbody tr:hover:not(:has(.empty)) { background: #f8fafc; }
.data-table th:first-child,
.data-table td:first-child { word-break: break-word; }
.data-table th:last-child,
.data-table td.row-actions { width: 140px; white-space: nowrap; }
.song-cell { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.row-actions-inner { display: flex; gap: 6px; align-items: center; justify-content: flex-start; }
.btn-sm { padding: 4px 12px; border-radius: var(--radius-round); font-size: 12px; border: 1px solid var(--border-color, #e2e8f0); background: #fff; cursor: pointer; transition: all 0.15s ease; }
.btn-sm:hover { border-color: var(--color-primary); color: var(--color-primary); }
.btn-sm.primary { background: var(--color-primary); color: #fff; border-color: var(--color-primary); }
.btn-sm.primary:hover { filter: brightness(1.05); }
.btn-sm.danger { color: #cf1322; border-color: #ffccc7; background: #fff; }
.btn-sm.danger:hover { background: #fff1f0; border-color: #ffa39e; }
.empty { text-align: center; color: var(--text-muted); padding: 36px !important; font-size: 13px; }
</style>
